"use client";

import type { ReactNode } from "react";
import {
  LessonRichContent,
  type LessonProgramMeta,
} from "@/features/lessons/components/LessonRichContent";
import { MermaidRenderer } from "@/components/mermaid-renderer";
import type { Locale } from "@/lib/i18n";
import type { HintLadderOptions } from "@/features/lessons/lib/hint-ladder";
import type { LearnReaderLesson } from "./api";
import { LearnLockCard } from "./LearnLockCard";
import { LearnProjectSubmit } from "./LearnProjectSubmit";

/**
 * The three access states of docs/35 §5, as one branch (`LN-RDR01`).
 *
 * It is its own component and not a ternary inside `LearnReaderClient` for one
 * reason: this is the decision the ticket has to prove, and a branch that sits
 * inside a component needing TanStack Query, the auth provider, the router and
 * a Monaco editor cannot be asserted in a unit test. Here it can, and
 * `LearnLessonBody.test.tsx` asserts all three states in both content languages.
 *
 * `granted` renders the body through `LessonRichContent`, the app's one lesson
 * renderer. The other two render the lock card and **no body at all**, which is
 * not a decision made here: the server sends no body on a locked read
 * (`learn-lesson.service.ts`), so there is nothing to hide even if this file
 * wanted to.
 */
export function LearnLessonBody({
  lesson,
  contentLang,
  locale,
  trackSlug,
  title,
  signInRedirect,
  accessPending = false,
  hintLadder,
  checkpoint,
  onRunCode,
  run,
  onEnhanced,
}: {
  lesson: LearnReaderLesson;
  contentLang: "en" | "bn";
  locale: Locale;
  trackSlug: string;
  title: string;
  signInRedirect: string;
  /** `LN-RDR03`: the lesson is locked in the cached page and the reader's own answer is still on its way. */
  accessPending?: boolean;
  hintLadder: HintLadderOptions;
  checkpoint?: ReactNode;
  onRunCode: (code: string, autoRun: boolean, meta: LessonProgramMeta) => void;
  /** Which code blocks carry a Run button (`LN-UI05`). */
  run?: { label: string; canRun: (lang: string | undefined, code: string) => boolean };
  /** The rendered body, once its chrome is in place: the reader reads its sections from it. */
  onEnhanced?: (root: HTMLElement) => void;
}) {
  if (lesson.access !== "granted") {
    return (
      <LearnLockCard
        access={lesson.access}
        learnAccess={lesson.learnAccess}
        contentLang={contentLang}
        pending={accessPending}
        lessonTitle={title}
        locale={locale}
        signInRedirect={signInRedirect}
        teaser={lesson.teaser}
        trackSlug={trackSlug}
      />
    );
  }

  return (
    <>
      <LessonRichContent
        html={lesson.content_en ?? undefined}
        htmlBn={lesson.content_bn ?? undefined}
        learn={{ hintLadder, checkpoint, onRunCode, run, onEnhanced, lang: contentLang }}
        locale={locale}
      />
      <MermaidRenderer />
      {/* `LN-ASG02`: a project lesson's body is its brief, rendered above like
          any lesson; the hand-in is the assignment workspace's, offered only
          when the server says that workspace opens for this reader. */}
      {lesson.project ? (
        <LearnProjectSubmit assignmentId={lesson.project.assignmentId} locale={locale} />
      ) : null}
    </>
  );
}
